/**
 * Pure logic for the region chart's playhead scrubber — read any hour of
 * "today" by dragging (or keyboard-stepping) across the curve.
 *
 * Deterministic and DOM-free (the component owns pointer/keyboard wiring and
 * pixel geometry): given the expanded today series it answers "what hour is the
 * playhead on, and what value sits there?" — snapping a fractional hour to the
 * nearest sampled hour with the vendored d3 `bisector`, and NEVER inventing a
 * value across a null gap (a gap hour reads back `null` ⇒ the UI shows "nessun
 * dato", honesty first). All unit-tested; the chart just renders the result.
 */

import { bisector } from "@visx/vendor/d3-array";
import type { TodayPoint } from "./regionChart";

/** Nearest-index bisector over the (ascending) today hours. */
const hourBisector = bisector<TodayPoint, number>((p) => p.hourLocal);

export interface ScrubBounds {
  /** First hour that carries a today reading — the left edge of the scrub range. */
  minHour: number;
  /** Last hour with a reading — "now", the right edge (never scrub into the future). */
  nowHour: number;
}

/**
 * The scrubbable hour range: first-known → now. `null` when today carries no
 * reading at all (⇒ no scrubber, the empty state stands).
 */
export function scrubBounds(points: readonly TodayPoint[]): ScrubBounds | null {
  let minHour: number | null = null;
  let nowHour: number | null = null;
  for (const p of points) {
    if (p.waiting === null) continue;
    if (minHour === null) minHour = p.hourLocal;
    nowHour = p.hourLocal;
  }
  if (minHour === null || nowHour === null) return null;
  return { minHour, nowHour };
}

export interface ScrubReading {
  /** The snapped hour (integer, within [minHour, maxHour]). */
  hour: number;
  /** Today's value at that hour, or `null` for a gap ("nessun dato"). */
  value: number | null;
}

/**
 * Snap a raw (possibly fractional) hour — e.g. from a pointer x — to the nearest
 * sampled today hour within [minHour, maxHour], and report the value there.
 * Uses the d3 bisector for the snap; a gap hour returns `value: null` so the
 * readout stays honest across missing data.
 */
export function scrubAt(
  points: readonly TodayPoint[],
  rawHour: number,
  minHour: number,
  maxHour: number,
): ScrubReading {
  const clampHour = (h: number) => Math.min(maxHour, Math.max(minHour, h));
  const clamped = clampHour(rawHour);
  if (points.length === 0) return { hour: clamped, value: null };
  const idx = hourBisector.center(points, clamped);
  const nearest = points[idx] ?? points[points.length - 1]!;
  const hour = clampHour(nearest.hourLocal);
  const at = points.find((p) => p.hourLocal === hour);
  return { hour, value: at ? at.waiting : null };
}

export type AnnotationKind = "busiest" | "quietest";

export interface ScrubAnnotation {
  hour: number;
  kind: AnnotationKind;
}

/**
 * The one or two editorial beats the playhead can reveal as it passes: today's
 * busiest hour and its quietest. Derived only from real today readings — no
 * fabrication. Returns `[]` for a flat or too-thin day (nothing worth pointing
 * at); the busiest is omitted-as-duplicate only when it coincides with the
 * quietest (same hour), which the flat-day guard already excludes.
 */
export function chartAnnotations(points: readonly TodayPoint[]): ScrubAnnotation[] {
  const withData = points.filter((p): p is TodayPoint & { waiting: number } => p.waiting !== null);
  if (withData.length < 2) return [];

  let busiest = withData[0]!;
  let quietest = withData[0]!;
  for (const p of withData) {
    if (p.waiting > busiest.waiting) busiest = p;
    if (p.waiting < quietest.waiting) quietest = p;
  }
  if (busiest.waiting === quietest.waiting) return []; // flat day — no story

  const out: ScrubAnnotation[] = [{ hour: busiest.hourLocal, kind: "busiest" }];
  if (quietest.hourLocal !== busiest.hourLocal) {
    out.push({ hour: quietest.hourLocal, kind: "quietest" });
  }
  return out;
}
